import { useCallback, useEffect, useRef, useState } from 'react'

// BCP-47 language tags for supported target languages
const LANG_TAGS: Record<string, string> = {
  en: 'en-US',
  ru: 'ru-RU',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  pt: 'pt-PT',
  zh: 'zh-CN',
  ja: 'ja-JP',
  ko: 'ko-KR',
  ar: 'ar-SA',
  tr: 'tr-TR',
  pl: 'pl-PL',
  nl: 'nl-NL',
}

function toLangTag(lang?: string | null): string {
  if (!lang) return 'en-US'
  return LANG_TAGS[lang.toLowerCase()] ?? lang
}

/**
 * Wraps the browser's Web Speech API (speechSynthesis).
 * Returns speak(), isSpeaking, and isSupported.
 * If the API is unavailable, isSupported=false and speak() is a no-op.
 */
export function useSpeech(lang?: string | null) {
  const isSupported =
    typeof window !== 'undefined' && 'speechSynthesis' in window

  const [isSpeaking, setIsSpeaking] = useState(false)
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null)

  // Cancel any in-flight utterance on unmount
  useEffect(() => {
    return () => {
      if (isSupported) window.speechSynthesis.cancel()
    }
  }, [isSupported])

  const speak = useCallback(
    (text: string, onEnd?: () => void) => {
      if (!isSupported || !text.trim()) {
        // Nothing to speak — call onEnd immediately so callers don't stall
        onEnd?.()
        return
      }

      // Cancel previous utterance before starting a new one
      window.speechSynthesis.cancel()

      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = toLangTag(lang)
      utterance.onstart = () => setIsSpeaking(true)
      utterance.onend = () => {
        setIsSpeaking(false)
        onEnd?.()
      }
      utterance.onerror = () => {
        setIsSpeaking(false)
        onEnd?.()
      }

      utteranceRef.current = utterance
      window.speechSynthesis.speak(utterance)
    },
    [isSupported, lang],
  )

  /**
   * Stop any in-flight speech WITHOUT triggering the onEnd callback.
   * Use this when the caller wants to advance manually and must not let
   * the speech completion path double-fire the same advance action.
   */
  const cancelSilently = useCallback(() => {
    if (!isSupported) return
    if (utteranceRef.current) {
      // Null out callbacks before cancel so the browser's onend event
      // (fired by some implementations when cancel() is called) does nothing.
      utteranceRef.current.onend = null
      utteranceRef.current.onerror = null
      utteranceRef.current = null
    }
    window.speechSynthesis.cancel()
    setIsSpeaking(false)
  }, [isSupported])

  return { speak, cancelSilently, isSpeaking, isSupported }
}
